import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';

const EditQRCode = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [formData, setFormData] = useState({
    originalUrl: '',
    title: '',
    description: '',
    iosDeepLink: '',
    iosAppStoreUrl: '',
    androidDeepLink: '',
    androidPlayStoreUrl: '',
    webUrl: '',
  });

  const [showAdvanced, setShowAdvanced] = useState(false);

  const fetchQRCode = useCallback(async () => {
    try {
      const response = await axios.get(`/api/qr/${id}`);
      const qr = response.data;
      setFormData({
        originalUrl: qr.original_url || '',
        title: qr.title || '',
        description: qr.description || '',
        iosDeepLink: qr.ios_deep_link || '',
        iosAppStoreUrl: qr.ios_app_store_url || '',
        androidDeepLink: qr.android_deep_link || '',
        androidPlayStoreUrl: qr.android_play_store_url || '',
        webUrl: qr.web_url || '',
      });
      
      // Show advanced if any smart redirect field has value
      if (qr.ios_deep_link || qr.ios_app_store_url || qr.android_deep_link || qr.android_play_store_url || qr.web_url) {
        setShowAdvanced(true);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load QR code');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchQRCode();
  }, [fetchQRCode]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    try {
      await axios.put(`/api/qr/${id}`, formData);
      setSuccess('QR code updated successfully!');
      setTimeout(() => {
        navigate(`/qr-codes/view/${id}`);
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update QR code');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading QR code...</div>;
  }

  return (
    <div className="container">
      <div className="dashboard-header">
        <h1 style={{ 
          background: 'var(--gradient-primary)', 
          WebkitBackgroundClip: 'text', 
          WebkitTextFillColor: 'transparent' 
        }}>
          Edit QR Code
        </h1>
        <Link to={`/qr-codes/view/${id}`} className="btn btn-secondary">
          ← Back to Details
        </Link>
      </div>

      <div style={{ maxWidth: '700px' }}>
        <div className="card">
          {success && <div className="success">{success}</div>}
          {error && <div className="error">{error}</div>}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Destination URL *</label>
              <input
                type="url"
                name="originalUrl"
                value={formData.originalUrl}
                onChange={handleChange}
                placeholder="https://example.com"
                required
              />
            </div>

            <div className="form-group">
              <label>Title</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="My QR Code"
              />
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Optional description for your QR code..."
                rows={3}
              />
            </div>

            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="btn btn-ghost"
              style={{ marginBottom: '24px', width: '100%', justifyContent: 'space-between' }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="3"/>
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                </svg>
                Smart Redirect Settings
              </span>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ transform: showAdvanced ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s' }}>
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </button>

            {showAdvanced && (
              <div style={{ 
                padding: '24px', 
                background: 'var(--bg-elevated)', 
                borderRadius: 'var(--radius-md)',
                marginBottom: '24px',
                border: '1px solid var(--border-color)'
              }}>
                <h3 style={{ marginBottom: '20px', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--accent-primary)" strokeWidth="2">
                    <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
                    <line x1="12" y1="18" x2="12.01" y2="18"/>
                  </svg>
                  iOS Settings
                </h3>
                <div className="form-group">
                  <label>iOS Deep Link</label>
                  <input
                    type="text"
                    name="iosDeepLink"
                    value={formData.iosDeepLink}
                    onChange={handleChange}
                    placeholder="myapp://path/to/content"
                  />
                </div>
                <div className="form-group">
                  <label>iOS App Store URL</label>
                  <input
                    type="url"
                    name="iosAppStoreUrl"
                    value={formData.iosAppStoreUrl}
                    onChange={handleChange}
                    placeholder="https://apps.apple.com/app/..."
                  />
                </div>

                <h3 style={{ marginBottom: '20px', marginTop: '28px', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--accent-tertiary)" strokeWidth="2">
                    <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
                    <line x1="12" y1="18" x2="12.01" y2="18"/>
                  </svg>
                  Android Settings
                </h3>
                <div className="form-group">
                  <label>Android Deep Link</label>
                  <input
                    type="text"
                    name="androidDeepLink"
                    value={formData.androidDeepLink}
                    onChange={handleChange}
                    placeholder="myapp://path/to/content"
                  />
                </div>
                <div className="form-group">
                  <label>Play Store URL</label>
                  <input
                    type="url"
                    name="androidPlayStoreUrl"
                    value={formData.androidPlayStoreUrl}
                    onChange={handleChange}
                    placeholder="https://play.google.com/store/apps/..."
                  />
                </div>

                <h3 style={{ marginBottom: '20px', marginTop: '28px', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--accent-secondary)" strokeWidth="2">
                    <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                    <line x1="8" y1="21" x2="16" y2="21"/>
                    <line x1="12" y1="17" x2="12" y2="21"/>
                  </svg>
                  Desktop Settings
                </h3>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Web URL (Desktop)</label>
                  <input
                    type="url"
                    name="webUrl"
                    value={formData.webUrl}
                    onChange={handleChange}
                    placeholder="https://example.com/desktop"
                  />
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving}
                style={{ flex: 1, padding: '16px' }}
              >
                {saving ? (
                  <>
                    <svg className="animate-spin" width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeDasharray="32" strokeDashoffset="32">
                        <animate attributeName="stroke-dashoffset" dur="1s" repeatCount="indefinite" values="32;0"/>
                      </circle>
                    </svg>
                    Saving...
                  </>
                ) : (
                  <>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                      <polyline points="17 21 17 13 7 13 7 21"/>
                      <polyline points="7 3 7 8 15 8"/>
                    </svg>
                    Save Changes
                  </>
                )}
              </button>
              <Link to={`/qr-codes/view/${id}`} className="btn btn-secondary" style={{ padding: '16px' }}>
                Cancel
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditQRCode;
